
import React, { useState, useEffect } from "react";
import "./EditEmployees.css";
import { API_URL } from "./API";

const EditEmployee = ({ employee, onBack, onUpdate }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    role: '',
    email: '',
    phone: '',
    dob: '',
    dateOfJoining: '',
    panNumber: '',
    aadharNumber: '',
    pfNumber: '',
    esiNumber: '',
    uanNumber: '',
    bankDetails: {
      accountName: '',
      accountNumber: '',
      ifscCode: '',
      branch: ''
    },
    dateOfRelieving: '',
    status: 'Active'
  });

  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canEditRole, setCanEditRole] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [validationErrors, setValidationErrors] = useState({});

  const checkRoleEditPermission = () => {
    const accessLevel = localStorage.getItem('accessLevel');
    const userRole = (localStorage.getItem('currentUserRole') || '').toUpperCase();
    const allowedRoles = ['CEO', 'ADMIN', 'ASSISTANT ADMIN'];
    return accessLevel === 'full' || allowedRoles.some(role => userRole.includes(role));
  };

  const isAdminUser = () => {
    const accessLevel = localStorage.getItem('accessLevel');
    const userRole = (localStorage.getItem('currentUserRole') || '').toUpperCase();
    const adminRoles = ['CEO', 'ADMIN', 'ASSISTANT ADMIN'];
    return accessLevel === 'full' || adminRoles.some(role => userRole.includes(role));
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await fetch(`${API_URL}/employees/get_employees`);
      if (response.ok) {
        const result = await response.json();
        let employeesData = [];
        if (Array.isArray(result)) {
          employeesData = result;
        } else if (result.employees && Array.isArray(result.employees)) {
          employeesData = result.employees;
        }
        setAllEmployees(employeesData);
        localStorage.setItem('allEmployeesData', JSON.stringify(employeesData));
        return employeesData;
      }
    } catch (error) {
     
    }
    return [];
  };

  const sendNotificationToAdmins = async (employeeName, changes) => {
    let employeesData = allEmployees;
    
    if (employeesData.length === 0) {
      const storedData = localStorage.getItem('allEmployeesData');
      if (storedData) {
        try {
          employeesData = JSON.parse(storedData);
        } catch (e) {
          employeesData = [];
        }
      }
    }
    
    if (employeesData.length === 0) {
      employeesData = await fetchAllEmployees();
    }

    const adminRoles = ['CEO', 'ADMIN', 'ASSISTANT ADMIN', 'CEO-SOFTWARE', 'CEO-SITE WORKERS', 'CEO - SOFTWARE', 'CEO - SITE WORKERS'];
    
    const adminEmployees = employeesData.filter(emp => {
      const empRole = (emp.role || '').toUpperCase();
      return adminRoles.some(role => empRole.includes(role.replace('-', ' ').replace('  ', ' ')));
    });

    const notification = {
      id: Date.now(),
      type: 'employee_update',
      message: `${employeeName} has updated their profile information`,
      details: {
        employeeName: employeeName,
        changes: changes
      },
      timestamp: new Date().toISOString(),
      read: false
    };

    if (adminEmployees.length > 0) {
      adminEmployees.forEach(admin => {
        const adminId = admin.employee_id || admin.id;
        const existingNotifications = JSON.parse(localStorage.getItem(`notifications_${adminId}`) || '[]');
        existingNotifications.unshift(notification);
        localStorage.setItem(`notifications_${adminId}`, JSON.stringify(existingNotifications));
      });
    } else {
      const adminKeys = ['1', '2', '3'];
      adminKeys.forEach(adminId => {
        const existingNotifications = JSON.parse(localStorage.getItem(`notifications_${adminId}`) || '[]');
        existingNotifications.unshift(notification);
        localStorage.setItem(`notifications_${adminId}`, JSON.stringify(existingNotifications));
      });
    }

    window.dispatchEvent(new Event('notificationUpdate'));
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'notifications_update',
      newValue: Date.now().toString()
    }));
  };

  const sendNotificationToEmployee = (employeeId, adminName, changes) => {
    const notification = {
      id: Date.now(),
      type: 'profile_updated_by_admin',
      message: `Your profile has been updated by ${adminName}`,
      details: {
        updatedBy: adminName,
        changes: changes
      },
      timestamp: new Date().toISOString(),
      read: false
    };

    const existingNotifications = JSON.parse(localStorage.getItem(`notifications_${employeeId}`) || '[]');
    existingNotifications.unshift(notification);
    localStorage.setItem(`notifications_${employeeId}`, JSON.stringify(existingNotifications));

    window.dispatchEvent(new Event('notificationUpdate'));
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'notifications_update',
      newValue: Date.now().toString()
    }));
  };

  useEffect(() => {
    fetchAllEmployees();
  }, []);

  useEffect(() => {
    const isCEO = () => {
      const accessLevel = localStorage.getItem('accessLevel');
      const userRole = (localStorage.getItem('currentUserRole') || '').toUpperCase();
      return accessLevel === 'full' || userRole.includes('CEO');
    };

    const canEditEmployee = (employeeId) => {
      if (isCEO()) return true;
      const currentUserId = localStorage.getItem('currentUserId');
      return parseInt(currentUserId) === parseInt(employeeId);
    };

    if (employee && !canEditEmployee(employee.id || employee.employee_id)) {
      alert('You do not have permission to edit this employee');
      onBack();
    }

    setCanEditRole(checkRoleEditPermission());
  }, [employee, onBack]);

  useEffect(() => {
    if (employee && (employee.id || employee.employee_id)) {
      const empId = employee.id || employee.employee_id;
      const storedImage = localStorage.getItem(`employee_${empId}_profileImage`);
      setProfileImage(storedImage);
    }
  }, [employee]);

  useEffect(() => {
    if (employee) {
   
      setFormData({
        id: employee.id || employee.employee_id || '',
        name: employee.name || '',
        role: employee.role || '',
        email: employee.email || '',
        phone: employee.phone || employee.phone_number || '',
        dob: employee.dob || '',
        dateOfJoining: employee.dateOfJoining || employee.date_of_joining || '',
        panNumber: employee.panNumber || employee.pan_number || '',
        aadharNumber: employee.aadharNumber || employee.aadhar_number || '',
        pfNumber: employee.pfNumber || employee.pf_number || '',
        esiNumber: employee.esiNumber || employee.esi_number || '',
        uanNumber: employee.uanNumber || employee.uan_number || '',
        bankDetails: {
          accountName: employee.bankDetails?.accountName || employee.account_holder_name || '',
          accountNumber: employee.bankDetails?.accountNumber || employee.bank_account_number || '',
          ifscCode: employee.bankDetails?.ifscCode || employee.ifsc_code || '',
          branch: employee.bankDetails?.branch || ''
        },
        dateOfRelieving: employee.dateOfRelieving || employee.date_of_relieving || '',
        status: employee.status || 'Active'
      });
    }
  }, [employee]);

  const formatAadharNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 12);
    const formatted = limited.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted;
  };

  
  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return null;
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }
    return null;
  };

  const validatePAN = (pan) => {
    if (!pan || pan.trim() === '') return null;
    const panUpper = pan.toUpperCase();
    if (panUpper.length !== 10) {
      return 'PAN number must be exactly 10 characters';
    }
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(panUpper)) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return null;
  };

  const validateAadhar = (aadhar) => {
    if (!aadhar || aadhar.trim() === '') return null;
    const aadharDigits = aadhar.replace(/\s/g, '');
    if (aadharDigits.length !== 12) {
      return 'Aadhaar number must be exactly 12 digits';
    }
    if (!/^\d{12}$/.test(aadharDigits)) {
      return 'Aadhaar number must contain only digits';
    }
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

   
    setValidationErrors(prev => ({
      ...prev,
      [name]: null
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      phone: value
    }));

  
    const error = validatePhone(value);
    setValidationErrors(prev => ({
      ...prev,
      phone: error
    }));
  };

  const handlePANChange = (e) => {
    const value = e.target.value.toUpperCase().slice(0, 10);
    setFormData(prev => ({
      ...prev,
      panNumber: value
    }));

   
    const error = validatePAN(value);
    setValidationErrors(prev => ({
      ...prev,
      panNumber: error
    }));
  };

  const handleAadharChange = (e) => {
    const formatted = formatAadharNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      aadharNumber: formatted
    }));

  
    const error = validateAadhar(formatted);
    setValidationErrors(prev => ({
      ...prev,
      aadharNumber: error
    }));
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: value
      }
    }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfileImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const { email, phone, panNumber, aadharNumber, bankDetails } = formData;
    const errors = {};


    if (email && email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (phone && phone.trim()) {
      const phoneError = validatePhone(phone);
      if (phoneError) {
        errors.phone = phoneError;
      }
    }

    if (panNumber && panNumber.trim()) {
      const panError = validatePAN(panNumber);
      if (panError) {
        errors.panNumber = panError;
      }
    }


    if (aadharNumber && aadharNumber.trim()) {
      const aadharError = validateAadhar(aadharNumber);
      if (aadharError) {
        errors.aadharNumber = aadharError;
      }
    }

    
    if (bankDetails.ifscCode && bankDetails.ifscCode.trim()) {
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(bankDetails.ifscCode.toUpperCase())) {
        errors.ifscCode = 'Please enter a valid IFSC code';
      }
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      alert(firstError);
      return false;
    }

    return true;
  };

  const updateEmployeeAPI = async (employeeData) => {
    try {
      const formData = new FormData();
      
      formData.append('employee_id', employeeData.id);
      formData.append('name', employeeData.name);
      formData.append('phone_number', employeeData.phone);
      formData.append('email', employeeData.email);
      formData.append('role', employeeData.role);
      
      if (employeeData.dob) {
        formData.append('dob', employeeData.dob);
      }
      
      if (employeeData.dateOfJoining) {
        formData.append('date_of_joining', employeeData.dateOfJoining);
      }
      
      formData.append('aadhar_number', employeeData.aadharNumber.replace(/\s/g, ''));
      formData.append('pan_number', employeeData.panNumber);
      formData.append('account_holder_name', employeeData.bankDetails.accountName);
      formData.append('bank_account_number', employeeData.bankDetails.accountNumber);
      formData.append('ifsc_code', employeeData.bankDetails.ifscCode);
      formData.append('status', employeeData.status);
      
      if (employeeData.pfNumber) {
        formData.append('pf_number', employeeData.pfNumber);
      }
      
      if (employeeData.esiNumber) {
        formData.append('esi_number', employeeData.esiNumber);
      }
      
      if (employeeData.uanNumber) {
        formData.append('uan_number', employeeData.uanNumber);
      }

      const response = await fetch(`${API_URL}/employees/create_update_employees`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      
      throw error;
    }
  };

  const getChangedFields = () => {
    const changes = [];
    const originalEmployee = employee;
    
    if (formData.name !== (originalEmployee.name || '')) changes.push('Name');
    if (formData.email !== (originalEmployee.email || '')) changes.push('Email');
    if (formData.phone !== (originalEmployee.phone || originalEmployee.phone_number || '')) changes.push('Phone');
    if (formData.role !== (originalEmployee.role || '')) changes.push('Role');
    if (formData.dob !== (originalEmployee.dob || '')) changes.push('Date of Birth');
    if (formData.dateOfJoining !== (originalEmployee.dateOfJoining || originalEmployee.date_of_joining || '')) changes.push('Date of Joining');
    if (formData.panNumber !== (originalEmployee.panNumber || originalEmployee.pan_number || '')) changes.push('PAN Number');
    if (formData.aadharNumber !== (originalEmployee.aadharNumber || originalEmployee.aadhar_number || '')) changes.push('Aadhar Number');
    if (formData.pfNumber !== (originalEmployee.pfNumber || originalEmployee.pf_number || '')) changes.push('PF Number');
    if (formData.esiNumber !== (originalEmployee.esiNumber || originalEmployee.esi_number || '')) changes.push('ESI Number');
    if (formData.uanNumber !== (originalEmployee.uanNumber || originalEmployee.uan_number || '')) changes.push('UAN Number');
    if (formData.status !== (originalEmployee.status || 'Active')) changes.push('Status');
    if (formData.bankDetails.accountName !== (originalEmployee.bankDetails?.accountName || originalEmployee.account_holder_name || '')) changes.push('Bank Account Name');
    if (formData.bankDetails.accountNumber !== (originalEmployee.bankDetails?.accountNumber || originalEmployee.bank_account_number || '')) changes.push('Bank Account Number');
    if (formData.bankDetails.ifscCode !== (originalEmployee.bankDetails?.ifscCode || originalEmployee.ifsc_code || '')) changes.push('IFSC Code');
    return changes;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const updatedEmployee = {
        ...formData,
        panNumber: formData.panNumber.toUpperCase(),
        profileImage: profileImage, 
        bankDetails: {
          ...formData.bankDetails,
          ifscCode: formData.bankDetails.ifscCode.toUpperCase()
        }
      };

      if (profileImage && formData.id) {
        localStorage.setItem(`employee_${formData.id}_profileImage`, profileImage);
      }

      const result = await updateEmployeeAPI(updatedEmployee);
      
      const changedFields = getChangedFields();
      const currentUserId = localStorage.getItem('currentUserId');
      const currentUserName = localStorage.getItem('currentUserName') || 'User';
      const isEditingSelf = parseInt(currentUserId) === parseInt(formData.id);
      const isAdmin = isAdminUser();

      if (changedFields.length > 0) {
        if (isAdmin) {
          if (!isEditingSelf) {
            sendNotificationToEmployee(formData.id, currentUserName, changedFields.join(', '));
          }
        } else {
          await sendNotificationToAdmins(formData.name, changedFields.join(', '));
        }
      }
      
      alert('Employee updated successfully!');
      
      if (onUpdate) {
        await onUpdate(result.data || updatedEmployee);
      }
      
      onBack();
      
    } catch (error) {
      
      alert(`Failed to update employee: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const originalEmployee = employee;
    const hasChanges = JSON.stringify(formData) !== JSON.stringify({
      id: originalEmployee.id || originalEmployee.employee_id || '',
      name: originalEmployee.name || '',
      role: originalEmployee.role || '',
      email: originalEmployee.email || '',
      phone: originalEmployee.phone || originalEmployee.phone_number || '',
      dob: originalEmployee.dob || '',
      dateOfJoining: originalEmployee.dateOfJoining || originalEmployee.date_of_joining || '',
      panNumber: originalEmployee.panNumber || originalEmployee.pan_number || '',
      aadharNumber: originalEmployee.aadharNumber || originalEmployee.aadhar_number || '',
      pfNumber: originalEmployee.pfNumber || originalEmployee.pf_number || '',
      esiNumber: originalEmployee.esiNumber || originalEmployee.esi_number || '',
      uanNumber: originalEmployee.uanNumber || originalEmployee.uan_number || '',
      bankDetails: {
        accountName: originalEmployee.bankDetails?.accountName || originalEmployee.account_holder_name || '',
        accountNumber: originalEmployee.bankDetails?.accountNumber || originalEmployee.bank_account_number || '',
        ifscCode: originalEmployee.bankDetails?.ifscCode || originalEmployee.ifsc_code || '',
        branch: originalEmployee.bankDetails?.branch || ''
      },
      dateOfRelieving: originalEmployee.dateOfRelieving || originalEmployee.date_of_relieving || '',
      status: originalEmployee.status || 'Active'
    });

    if (hasChanges) {
      const confirmDiscard = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmDiscard) {
        return;
      }
    }
    
    onBack();
  };

  return (
    <div className="EE-00">
      <div className="EE-01">
        <h2 className="EE-02">Edit Employee - {employee?.name}</h2>

        <div className="EE-03">
          <div>
            <label className="EE-07">Employee Name</label> 
            <br />
            <input 
              type="text" 
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter Employee Name" 
              className="EE-08"
              disabled={loading}
            />
          </div>

          <div>
            <label className="EE-09">Role</label> 
            <br />
            {canEditRole ? (
              <select 
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="EE-10"
                disabled={loading}
              >
                <option value="">Select Role</option>
                <option value="Admin">Admin</option>
                <option value="Junior Technician">Junior Technician</option>
                <option value="Helper">Helper</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Technician">Technician</option>
                <option value="Kit Operator">Kit Operator</option>
                <option value="Senior Engineer">Senior Engineer</option>
                <option value="CEO - Software">CEO - Software</option>
                <option value="Site Engineer">Site Engineer</option>
                <option value="Managing Director">Managing Director</option>
                <option value="Assistant Admin">Assistant Admin</option>
                <option value="Full Stack Developer">Full Stack Developer</option>
                <option value="Front-End Developer">Front-End Developer</option>
                <option value="Backend Developer">Backend Developer</option>
                <option value="Manual Testing">Manual Testing</option>
                <option value="CEO-Site Workers">CEO-Site Workers</option>
              </select>
            ) : (
              <input 
                type="text"
                value={formData.role}
                className="EE-10"
                disabled
                readOnly
              />
            )}
          </div>

          <div>
            <label className="EE-11">Email</label> 
            <br />
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter Employee's Email ID" 
              className="EE-12"
              disabled={loading}
              style={validationErrors.email ? { borderColor: 'red' } : {}}
            />
            {validationErrors.email && (
              <span style={{ color: 'red', fontSize: '12px' }}>{validationErrors.email}</span>
            )}
          </div>

          <div>
            <label className="EE-13">Phone Number</label> 
            <br />
            <input 
              type="text" 
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Enter 10-digit Phone Number" 
              className="EE-14"
              disabled={loading}
              maxLength="10"
              style={validationErrors.phone ? { borderColor: 'red' } : {}}
            />
            {validationErrors.phone && (
              <span style={{ color: 'red', fontSize: '12px' }}>{validationErrors.phone}</span>
            )}
          </div>

          <div>
            <label className="EE-07">Date of Birth</label> 
            <br />
            <input 
              type="date" 
              name="dob"
              value={formData.dob}
              onChange={handleInputChange}
              className="EE-08"
              disabled={loading}
            />
          </div>

          <div>
            <label className="EE-07">Date of Joining</label> 
            <br />
            <input 
              type="date" 
              name="dateOfJoining"
              value={formData.dateOfJoining}
              onChange={handleInputChange}
              className="EE-08"
              disabled={loading}
            />
          </div>

          <div className="EE-04">
            <label className="EE-05">Profile Image</label> 
            <br />
            <input 
              type="file" 
              className="EE-06"
              accept=".jpg,.jpeg,.png"
              disabled={loading}
              onChange={handleProfileImageChange}
            />
          </div>

          <div className="EE-15">
            <div className="EE-16">
              <label className="EE-17">Pan Number</label> 
              <br />
              <input 
                type="text" 
                name="panNumber"
                value={formData.panNumber}
                onChange={handlePANChange}
                placeholder="ABCDE1234F (10 characters)" 
                className="EE-19"
                disabled={loading}
                maxLength="10"
                style={{ 
                  textTransform: 'uppercase',
                  ...(validationErrors.panNumber ? { borderColor: 'red' } : {})
                }}
              />
              {validationErrors.panNumber && (
                <span style={{ color: 'red', fontSize: '12px' }}>{validationErrors.panNumber}</span>
              )}
            </div>
          </div>

          <div className="EE-20">
            <div className="EE-21">
              <label className="EE-22">Pan Upload</label> 
              <br />
              <input 
                type="file" 
                className="EE-23"
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={loading}
              />
            </div>
          </div>

          <div className="EE-24">
            <div className="EE-25">
              <label className="EE-26">Aadhar Number</label> 
              <br />
              <input 
                type="text" 
                name="aadharNumber"
                value={formData.aadharNumber}
                onChange={handleAadharChange}
                placeholder="1234 5678 9012 (12 digits)" 
                className="EE-28"
                disabled={loading}
                maxLength="14"
                style={validationErrors.aadharNumber ? { borderColor: 'red' } : {}}
              />
              {validationErrors.aadharNumber && (
                <span style={{ color: 'red', fontSize: '12px' }}>{validationErrors.aadharNumber}</span>
              )}
            </div>
          </div>

          <div className="EE-29">
            <div className="EE-30">
              <label className="EE-31">Aadhar Upload</label> 
              <br />
              <input 
                type="file" 
                className="EE-32"
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={loading}
              />
            </div>
          </div>

          <div className="EE-33">
            <label className="EE-34">Bank Details</label>  
            <br />
            <div className="EE-36">
              <input 
                type="text" 
                name="accountName"
                value={formData.bankDetails.accountName}
                onChange={handleBankDetailsChange}
                placeholder="A/C Name" 
                className="EE-37"
                disabled={loading}
              />
              <input 
                type="text" 
                name="accountNumber"
                value={formData.bankDetails.accountNumber}
                onChange={handleBankDetailsChange}
                placeholder="A/C Number" 
                className="EE-38"
                disabled={loading}
              />
              <input 
                type="text" 
                name="ifscCode"
                value={formData.bankDetails.ifscCode}
                onChange={handleBankDetailsChange}
                placeholder="IFSC Code" 
                className="EE-39"
                disabled={loading}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>

          <div className="EE-41">
            <label className="EE-42">Bank Upload</label>
            <input 
              type="file" 
              className="EE-43"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={loading}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              <div>
                <label className="EE-07">PF No</label> 
                <br />
                <input 
                  type="text" 
                  name="pfNumber"
                  value={formData.pfNumber}
                  onChange={handleInputChange}
                  placeholder="Enter PF Number" 
                  className="EE-08"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="EE-07">ESI No</label> 
                <br />
                <input 
                  type="text" 
                  name="esiNumber"
                  value={formData.esiNumber}
                  onChange={handleInputChange}
                  placeholder="Enter ESI Number" 
                  className="EE-08"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="EE-07">UAN No</label> 
                <br />
                <input 
                  type="text" 
                  name="uanNumber"
                  value={formData.uanNumber}
                  onChange={handleInputChange}
                  placeholder="Enter UAN Number" 
                  className="EE-08"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label className="EE-45">Date of Relieving</label> 
                <br />
                <input 
                  type="date" 
                  name="dateOfRelieving"
                  value={formData.dateOfRelieving}
                  onChange={handleInputChange}
                  className="EE-46"
                  disabled={loading}
                />
              </div>  

              <div>
                <label className="EE-48">Status</label> 
                <br />
                <select 
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="EE-49"
                  disabled={loading}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div> 

        <div className="EE-50">
          <button 
            className="EE-51" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
          <button 
            className="EE-52" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEmployee;